<template>
  <VueJsModalPrimary
    v-bind="getModalAttrs"
    v-on="$listeners"
    @before-close="beforeClose"
    @before-open="beforeOpen"
  >
    <BeatLoaderPrimary
      v-if="isPending || isDeleting"
      :style="style.loader"
    >
      <template v-slot:footer>
        <div :style="style.loaderFooter">
          <ButtonSecondary
            v-if="isPending"
            :truncate="true"
            @click="undo"
          >
            Undo deleting {{ tenant.name }}
          </ButtonSecondary>
          <TextRegular v-if="isDeleting">
            Deleting workspace...
          </TextRegular>
        </div>
      </template>
    </BeatLoaderPrimary>
    <div
      v-else
      :style="style.container"
    >
      <HeaderLargePrimary :style="style.header">
        Are you sure?
      </HeaderLargePrimary>
      <div :style="style.innerContainer">
        <TextRegular :isInline="true">
          This action cannot be undone. This will permanently delete the
        </TextRegular>
        <TextMediumAttention :isInline="true">
          {{ tenant.name }}
        </TextMediumAttention>
        <TextRegular :isInline="true">
          workspace, including all user data and records. Please type
        </TextRegular>
        <TextMediumAttention :isInline="true">
          {{ tenant.name }}
        </TextMediumAttention>
        <TextRegular :isInline="true">
          to confirm.
        </TextRegular>
        <InputSecondary
          v-model="input"
          placeholder="Enter workspace name"
          :style="style.input"
        />
      </div>
      <div :style="style.footer">
        <ButtonSecondary
          @click="close"
          :style="style.button"
        >
          Cancel
        </ButtonSecondary>
        <ButtonWideWarning
          :disabled="disabled"
          :truncate="true"
          @click="startDeletingTenant"
          :style="style.buttonWide"
        >
          Yes, delete this workspace permanently
        </ButtonWideWarning>
      </div>
    </div>
  </VueJsModalPrimary>
</template>

<script>
  import ApiTenant from '@js/api/tenant'
  import BeatLoaderPrimary from '@modules/v2/loader/beat-loader-primary'
  import ButtonSecondary from '@modules/v2/button/button-secondary'
  import ButtonWideWarning from '@modules/v2/button/button-wide-warning'
  import HeaderLargePrimary from '@modules/v2/header/header-large-primary'
  import InputSecondary from '@modules/v2/input/input-secondary'
  import TextMediumAttention from '@modules/v2/text/text-medium-attention'
  import TextRegular from '@modules/v2/text/text-regular'
  import VueJsModalPrimary from '@modules/v2/modal/vue-js-modal-primary'
  import { Layout } from '@js/styles'
  import { mapState } from 'vuex'
  import { MODAL_WINDOW_NAMES } from '@js/constants'

  export default {
    name: 'ModalDeleteTenant',
    inheritAttrs: false,
    components: {
      BeatLoaderPrimary,
      ButtonSecondary,
      ButtonWideWarning,
      HeaderLargePrimary,
      InputSecondary,
      TextMediumAttention,
      TextRegular,
      VueJsModalPrimary,
    },
    data: function () {
      return {
        input: '',
        isDeleting: false,
        isPending: false,
        name: MODAL_WINDOW_NAMES.DELETE_TENANT_PRIMARY,
        timerDeleting: null,
        timerDeletingPeriod: 5000,
        timerPending: null,
        timerPendingPeriod: 8000,
        style: {
          button: {
            padding: `0 ${Layout.space.sm}`,
          },
          buttonWide: {
            flex: 1,
            maxWidth: '324px',
            padding: `0 ${Layout.space.sm}`,
          },
          container: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between',
            padding: `${Layout.space.lg} ${Layout.space.md}`,
            width: '100%',
          },
          footer: {
            alignItems: 'flex-end',
            display: 'flex',
            justifyContent: 'space-between',
            margin: '36px 0 0 0',
            width: '100%',
          },
          header: {
            margin: '0 0 36px 0',
            padding: `0 ${Layout.space.sm}`,
          },
          innerContainer: {
            padding: `0 ${Layout.space.sm}`,
          },
          input: {
            margin: `${Layout.space.lg} 0 0 0`,
          },
          loader: {
            height: '295px',
            width: '100%',
          },
          loaderFooter: {
            justifyContent: 'center',
            display: 'flex',
            width: '100%',
          },
        },
      };
    },
    computed: {
      ...mapState({
        tenant: state => state.tenant,
      }),
      disabled: function () {
        return (
          this.isDeleting ||
          this.isPending ||
          this.input.length === 0 ||
          this.tenant.name !== this.input
        );
      },
      getModalAttrs: function () {
        return {
          ...this.$attrs,
          height: 'auto',
          maxHeight: 300,
          maxWidth: 480,
          minHeight: 300,
          width: '90%',
          name: this.name,
          clickToClose: !this.isDeleting,
        };
      },
    },
    methods: {
      beforeClose: function (e) {
        if (this.isPending || this.isDeleting) {
          this.$store.commit(
            'notification/setWarning',
            'Deleting workspace was cancelled.',
          );
        }
        this.clearState();
      },
      beforeOpen: function (e) {
        this.clearState();
      },
      clearState: function () {
        clearTimeout(this.timerDeleting);
        clearTimeout(this.timerPending);
        this.timerDeleting = null;
        this.timerPending = null;
        this.input = '';
        this.isPending = false;
        this.isDeleting = false;
      },
      close: function () {
        this.$modal.hide(this.name);
      },
      deleteTenant: async function () {
        try {
          const api = await new ApiTenant(this.tenant.domain);
          const res = await api.delete();
          await this.$store.commit('tenant/initialize');
        } catch (error) {
          console.error(error);
          this.$store.commit(
            'notification/setWarning',
            'Failed to delete workspace.',
          );
        } finally {
          this.clearState();
        }
      },
      startDeletingTenant: function () {
        this.isPending = true;
        this.isDeleting = false;
        clearTimeout(this.timerDeleting);
        clearTimeout(this.timerPending);
        this.timerPending = setTimeout(() => {
          this.isDeleting = true;
          this.isPending = false;
          this.timerDeleting = setTimeout(async () => {
            await this.deleteTenant();
          }, this.timerDeletingPeriod);
        }, this.timerPendingPeriod);
      },
      undo: function () {
        this.clearState();
      },
    },
  };
</script>
